import React, { forwardRef, useState, useEffect } from 'react';

const SubCategories = forwardRef(
  (
    {
      subCategories,
      activeSubCategory,
      onSelectSubCategory,
      hasHigherCategories = true,
      fromKiosk = false,
    },
    ref,
  ) => {
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(false);

    const getCategoryName = (category) => {
      return category.category || category.Category || category.name;
    };

    const checkScrollPosition = () => {
      if (!ref?.current) return;
      const { scrollLeft, scrollWidth, clientWidth } = ref.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
    };

    useEffect(() => {
      checkScrollPosition();
      const container = ref?.current;
      if (container) {
        container.addEventListener('scroll', checkScrollPosition);
        window.addEventListener('resize', checkScrollPosition);
        return () => {
          container.removeEventListener('scroll', checkScrollPosition);
          window.removeEventListener('resize', checkScrollPosition);
        };
      }
    }, [ref, subCategories]);

    const scroll = (direction) => {
      if (!ref?.current) return;
      ref.current.scrollBy({
        left: direction === 'left' ? -200 : 200,
        behavior: 'smooth',
      });
    };

    if (subCategories.length === 0) return null;

    return (
      <nav
        className={`sticky z-10 bg-white ${
          hasHigherCategories
            ? fromKiosk
              ? '!top-[18px] sm:!top-[28px] md:!top-[33px]'
              : '!top-[90px] sm:!top-[100px] md:!top-[105px]'
            : fromKiosk
              ? '!top-[0px] sm:!top-[0px] md:!top-[0px]'
              : '!top-[45px] sm:!top-[40px] md:!top-[40px]'
        }`}
      >
        <div className="relative">
          {showLeftArrow && (
            <>
              <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
              <button
                onClick={() => scroll('left')}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-20 p-1 bg-white rounded-full shadow-md hover:bg-gray-100"
              >
                <i className="fa fa-chevron-left text-sm sm:text-base text-gray-500" />
              </button>
            </>
          )}

          {showRightArrow && (
            <>
              <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />
              <button
                onClick={() => scroll('right')}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-20 p-1 bg-white rounded-full shadow-md hover:bg-gray-100"
              >
                <i className="fa fa-chevron-right text-sm sm:text-base text-gray-500" />
              </button>
            </>
          )}

          <div
            ref={ref}
            className="flex space-x-1 sm:space-x-2 pt-1 pb-0 sm:!pt-4 border-b border-[#f5f5f5] overflow-x-auto scrollbar-hide"
          >
            {subCategories.map((category) => {
              const categoryName = getCategoryName(category);
              const compositeId = `${category.higherCategoryId}_${categoryName}`;
              const isActive = activeSubCategory === compositeId;

              return (
                <button
                  key={compositeId}
                  data-active={isActive}
                  onClick={() => onSelectSubCategory(category)}
                  className={`
                    px-3 py-[12px] text-sm sm:!px-4 sm:!py-2 sm:text-base md:text-lg font-semibold whitespace-nowrap flex-shrink-0
                    hover:bg-gray-100
                    ${
                      isActive
                        ? 'text-primary border-b-2 border-primary'
                        : 'text-gray-700 border-b-2 border-transparent'
                    }
                  `}
                >
                  {categoryName || 'N/A'}
                </button>
              );
            })}
          </div>
        </div>
      </nav>
    );
  },
);

SubCategories.displayName = 'SubCategories';

export default SubCategories;
